import api from "utils/api";
import { orderBboxItemsByHilbertCurve } from "utils/math/orderBboxItemsByHilbertCurve";
import {
  groupedElementsFromCounts,
  buildRouteGroupsForPlan,
} from "../../shared/transforms";
import { sortByPrefixGroupedDesc } from "./sortByPrefixGroupedDesc";
import { sumElementMultipliers } from "./sumElementMultipliers";
import { DEFAULT_IMAGE_SCALE_FACTOR } from "../../SmartSurveyorForRudolfFritz/hooks/constants";
import { isPageRotation } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelWrapper/createDisplayTransform";
import type { PageRotation } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelWrapper/createDisplayTransform";
import type { FloorPlanRecord } from "../../SmartSurveyorForRudolfFritz/hooks/types";
import type { PageDimensions, PageScaleSource, PlanV3 } from "./types";

interface FetchSingleFloorPlanArgs {
  record: FloorPlanRecord;
  index: number;
}

export const fetchSingleFloorPlan = async ({
  record,
  index,
}: FetchSingleFloorPlanArgs): Promise<PlanV3> => {
  const {
    counts,
    routes,
    selectionAreas = [],
  } = await api.getQuantitySurveyorFloorPlanStructuredData(record.id);

  const pageScaleFactors = new Map<number, number>();
  const rotationByPageIndex = new Map<number, PageRotation>();
  const pageIdByIndex = new Map<number, number>();
  const metersPerPdfPointByPageIndex = new Map<number, number>();
  const metersPerPdfPointSourceByPageIndex = new Map<
    number,
    PageScaleSource | null
  >();
  const detectedMetersPerPdfPointByPageIndex = new Map<number, number | null>();
  const pageDimensionsByIndex = new Map<number, PageDimensions>();
  for (const page of counts.pages ?? []) {
    pageScaleFactors.set(
      page.index,
      page.image_scale_factor ?? DEFAULT_IMAGE_SCALE_FACTOR,
    );
    rotationByPageIndex.set(
      page.index,
      isPageRotation(page.rotation) ? page.rotation : 0,
    );
    pageIdByIndex.set(page.index, page.id);
    metersPerPdfPointByPageIndex.set(
      page.index,
      page.meters_per_pdf_point ?? 0,
    );
    metersPerPdfPointSourceByPageIndex.set(
      page.index,
      page.meters_per_pdf_point_source ?? null,
    );
    detectedMetersPerPdfPointByPageIndex.set(
      page.index,
      page.detected_meters_per_pdf_point ?? null,
    );
    if (
      typeof page.width_px === "number" &&
      typeof page.height_px === "number"
    ) {
      pageDimensionsByIndex.set(page.index, {
        widthPx: page.width_px,
        heightPx: page.height_px,
      });
    }
  }

  const firstPage = counts.pages?.[0];

  // Only backend-signed CloudFront URLs are usable; an unsigned bare key
  // means signing is unavailable and the asset stays unreachable by design.
  const firstPageImage = firstPage?.image ?? "";
  const planImage = firstPageImage.startsWith("http") ? firstPageImage : "";

  const firstPagePdf = firstPage?.pdf ?? "";
  const pdfUrl = firstPagePdf.startsWith("http") ? firstPagePdf : "";

  // Coordinates are stored in PDF space — pass sf=1 to keep them unscaled.
  const elementsScaleFactor = 1;

  const groupedElements = groupedElementsFromCounts(
    counts,
    elementsScaleFactor,
  );

  // Summed once per group; the sort comparator would otherwise re-sum on every comparison.
  const elementQuantityByGroupId = new Map(
    groupedElements.groupIds.map((groupId) => [
      groupId,
      sumElementMultipliers({
        elements: groupedElements.byGroupId[groupId] ?? [],
      }),
    ]),
  );

  groupedElements.groupIds = sortByPrefixGroupedDesc({
    items: groupedElements.groupIds,
    getLabel: (groupId) => groupId,
    getSize: (groupId) => elementQuantityByGroupId.get(groupId) ?? 0,
  });

  for (const groupId of Object.keys(groupedElements.byGroupId)) {
    groupedElements.byGroupId[groupId] = orderBboxItemsByHilbertCurve({
      elements: groupedElements.byGroupId[groupId],
    });
  }

  const groupMeta: PlanV3["groupMeta"] = {};
  for (const group of counts.groups ?? []) {
    groupMeta[group.name ?? "(No Group)"] = {
      element_type_id: group.element_type_id!,
      nameStatus: group.name_status,
      lv_position: group.lv_position,
      is_partial: group.is_partial,
      lvMatch: group.lvMatch ?? undefined,
    };
  }

  const routeGroups = sortByPrefixGroupedDesc({
    items: buildRouteGroupsForPlan(routes.routes ?? [], elementsScaleFactor),
    getLabel: (routeGroup) => routeGroup.label,
    getSize: (routeGroup) => routeGroup.meters,
  });

  return {
    index,
    label: counts.plan_name ?? record.name,
    planImage,
    pdfUrl,
    floorPlanId: record.id,
    pageIdByIndex,
    groupedElements,
    groupMeta,
    routeGroups,
    selectionAreas,
    pageScaleFactors,
    rotationByPageIndex,
    pageDimensionsByIndex,
    metersPerPdfPointByPageIndex,
    metersPerPdfPointSourceByPageIndex,
    detectedMetersPerPdfPointByPageIndex,
    imageWidthPx: firstPage?.width_px,
    imageHeightPx: firstPage?.height_px,
  };
};
